import { Box } from '@mui/material';
import {
  MyProjectsList,
  UpcomingAllocationsList,
  type MyProjectRow,
  type UpcomingAllocationRow,
} from '../../dashboard-widgets';
import type { MyAllocationItem } from '../../dashboard-widgets/types';

interface AllocationsSectionProps {
  allocations: MyAllocationItem[] | undefined;
  loading?: boolean;
  onOpenProject?: (projectId: string) => void;
}

/**
 * `Allocations` section (NEW in Phase 3b — see SPEC restructure).
 *
 * Splits `widgets.myAllocations` into two row-lists, prototype-shaped:
 *   - **My projects** — current allocations (start ≤ today ≤ end)
 *   - **Upcoming allocations** — future allocations (start > today)
 *
 * Section gate: always-on (registry-side).
 */
export function AllocationsSection({
  allocations,
  loading,
  onOpenProject,
}: AllocationsSectionProps) {
  const now = Date.now();
  const all = allocations ?? [];

  const current: MyProjectRow[] = [];
  const upcoming: UpcomingAllocationRow[] = [];

  for (const a of all) {
    const start = parseISO(a.startDate);
    const end = parseISO(a.endDate);
    if (start == null || end == null) continue;

    if (start <= now && end >= now) {
      current.push({
        id: a.allocationId,
        projectId: a.projectId,
        projectName: a.projectName,
        clientName: a.company?.name ?? null,
        company: a.company ?? null,
        allocationSummary: formatCurrentSummary(a),
      });
    } else if (start > now) {
      upcoming.push({
        id: a.allocationId,
        projectId: a.projectId,
        projectName: a.projectName,
        clientName: a.company?.name ?? null,
        company: a.company ?? null,
        startsLabel: `Starts ${formatDate(start)}`,
        percentage: a.percentage,
      });
    }
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
        gap: 2,
        alignItems: 'start',
      }}
    >
      <MyProjectsList
        rows={current}
        loading={loading}
        onOpenProject={onOpenProject}
      />
      <UpcomingAllocationsList rows={upcoming} loading={loading} />
    </Box>
  );
}

function parseISO(iso: string): number | null {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

function formatCurrentSummary(a: MyAllocationItem): string {
  const end = parseISO(a.endDate);
  const untilLabel = end != null ? `until ${formatDate(end)}` : '';
  return [`${a.percentage}%`, untilLabel].filter(Boolean).join(' · ');
}

function formatDate(t: number): string {
  return new Date(t).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
  });
}
